// LIGHTPAINT : les contours principaux d'une pochette, extraits une fois par
// morceau dans un Web Worker.
//
// extractContours() est AUTONOME : aucune référence extérieure, aucune syntaxe
// postérieure à ES2017 que esbuild devrait réécrire avec un helper. Le worker
// est construit en texte à partir de sa source (Function.prototype.toString),
// et la même fonction tourne sous Node dans les tests.
//
// La chaîne, sur une image N x N (192) :
//   1. luminance ; médiane 5x5 puis bilatéral 5x5 (sigma 2 px, 22 niveaux) :
//      le grain et le JPEG disparaissent, les bords restent francs ;
//   2. postérisation en 4 niveaux aux quantiles 25/50/75 %. Une pochette à
//      deux tons (Unknown Pleasures : 90 % de noir) met les trois quantiles
//      dans le noir et ferait tracer le bruit : les seuils à moins de 12
//      niveaux l'un de l'autre ou des extrêmes (p2, p98) sont remplacés par
//      le découpage régulier de [p2, p98] ;
//   3. marching squares sur le champ filtré à chaque seuil (iso = seuil + 0,5,
//      jamais égal à un aplat) : contours sous-pixel, ouverts au bord ;
//   4. filtre : moins de 6 % du périmètre de l'image, ou fermé, petit (aire
//      < 0,4 %) et irrégulier (P²/A > 18 ; cercle 12,6, carré 16) : dehors.
//      C'est ce qui retire les lettres. Les lignes de texte fusionnées
//      (tortuosité) et les iso-lignes dans un dégradé doux (netteté moyenne
//      du bord sous 16 niveaux/px) sortent aussi ;
//   5. NON-REDONDANCE (en plus du brief) : un bord franc traverse les trois
//      seuils au même endroit et donnait trois traits parallèles à moins
//      d'un pixel. Du plus long au plus court, chaque contour est découpé
//      en morceaux qui ne passent pas à moins de 1,6 px d'un contour déjà
//      gardé ; seuls les morceaux assez longs restent ;
//   6. les plus longs jusqu'à 12 fois le côté de l'image en tout ;
//   7. Douglas-Peucker 0,8 px puis Chaikin x2 ;
//   8. ordre : du plus long au plus court, répartis entre DEUX sources par
//      longueur cumulée équilibrée ; chaque trait d'une source commence par
//      l'extrémité la plus proche de la fin du précédent (un contour fermé
//      commence à son point le plus proche) ;
//   9. couleur : moyenne le long du trait, prise de chaque côté du bord à
//      2 px, du côté le plus coloré.
// Sortie : 4096 points (x, y en [-1, 1], y vers le haut ; n° du trait dans
// l'ordre de dessin ; 1 = lampe éteinte avant ce point), la table des
// longueurs cumulées, les traits, l'image postérisée (debug).

export const CONTOUR_N = 192;
export const MAX_POINTS = 4096;

export function extractContours(rgba, N) {
  var NN = N * N;
  var i, x, y, k, j;
  function luminance(rgba, NN, lum) {
    for (var q = 0; q < NN; q++) lum[q] = (rgba[4 * q] * 54 + rgba[4 * q + 1] * 183 + rgba[4 * q + 2] * 19) >> 8;
  }
  function histogram(f, NN, hist) {
    for (var q = 0; q < NN; q++) hist[(f[q] + 0.5) | 0]++;
  }
  function posterise(f, NN, th, poster) {
    var t0 = th.length > 0 ? th[0] : 1e9, t1 = th.length > 1 ? th[1] : 1e9, t2 = th.length > 2 ? th[2] : 1e9;
    for (var q = 0; q < NN; q++) { var v = f[q]; poster[q] = v > t2 ? 3 : (v > t1 ? 2 : (v > t0 ? 1 : 0)); }
  }
  // median 5x5 (borders clamped), EXACT, by Huang's sliding histogram: per
  // row, 5 values leave and 5 enter the 256-bin histogram at each step and
  // the median moves by a few bins (a sort of the 25 values per pixel was
  // the costliest step of the chain)
  function medianHuang(lum, N, med) {
    var hist5 = new Int32Array(256), rows = new Int32Array(5);
    var x, y, k;
    for (y = 0; y < N; y++) {
      for (k = 0; k < 5; k++) { var ry = y + k - 2; rows[k] = (ry < 0 ? 0 : (ry >= N ? N - 1 : ry)) * N; }
      hist5.fill(0);
      for (var wx = -2; wx <= 2; wx++) {
        var cx0 = wx < 0 ? 0 : wx;
        for (k = 0; k < 5; k++) hist5[lum[rows[k] + cx0]]++;
      }
      var m = 0, lt = 0;
      while (lt + hist5[m] <= 12) { lt += hist5[m]; m++; }
      for (x = 0; x < N; x++) {
        med[y * N + x] = m;
        if (x === N - 1) break;
        var xo = x - 2 < 0 ? 0 : x - 2, xi = x + 3 >= N ? N - 1 : x + 3;
        for (k = 0; k < 5; k++) {
          var vo = lum[rows[k] + xo], vi = lum[rows[k] + xi];
          hist5[vo]--; if (vo < m) lt--;
          hist5[vi]++; if (vi < m) lt++;
        }
        while (lt > 12) { m--; lt -= hist5[m]; }
        while (lt + hist5[m] <= 12) { lt += hist5[m]; m++; }
      }
    }
  }
  // bilateral 5x5 (sigma 2 px, 22 levels); the border rows and columns
  // clamp, the interior reads straight
  function bilateral(med, N, f) {
    var ws = new Float32Array(25), wr = new Float32Array(256), offs = new Int32Array(25);
    var x, y, k;
    for (k = 0; k < 25; k++) { var ddx = (k % 5) - 2, ddy = ((k / 5) | 0) - 2; ws[k] = Math.exp(-(ddx * ddx + ddy * ddy) / 8); offs[k] = ddy * N + ddx; }
    for (k = 0; k < 256; k++) wr[k] = Math.exp(-(k * k) / (2 * 22 * 22));
    for (y = 0; y < N; y++) for (x = 0; x < N; x++) {
      var o0 = y * N + x, c = med[o0], sw = 0, sv = 0, w, vb, d;
      if (x >= 2 && y >= 2 && x < N - 2 && y < N - 2) {
        for (k = 0; k < 25; k++) {
          vb = med[o0 + offs[k]];
          d = vb - c; if (d < 0) d = -d;
          w = ws[k] * wr[d];
          sw += w; sv += w * vb;
        }
      } else {
        for (k = 0; k < 25; k++) {
          var yb = y + ((k / 5) | 0) - 2, xb = x + (k % 5) - 2;
          if (yb < 0) yb = 0; else if (yb >= N) yb = N - 1;
          if (xb < 0) xb = 0; else if (xb >= N) xb = N - 1;
          vb = med[yb * N + xb];
          d = vb - c; if (d < 0) d = -d;
          w = ws[k] * wr[d];
          sw += w; sv += w * vb;
        }
      }
      f[o0] = sv / sw;
    }
  }
  // Marching squares on f at iso. Edge ids: 2(yN + x) the horizontal edge
  // from (x, y) to (x+1, y), + 1 the vertical one from (x, y) to (x, y+1);
  // each crossing joins at most two cells, so a contour is a walk through
  // the edges' two neighbours. Open contours (ending on the border) are
  // walked from their ends first, then the loops.
  function isoContours(f, N, iso, ms, raw) {
    var ex = ms.ex, ey = ms.ey, nb1 = ms.nb1, nb2 = ms.nb2, seen = ms.seen;
    var NN = N * N, x, y, e;
    nb1.fill(-1); nb2.fill(-1); seen.fill(0);
    for (y = 0; y < N - 1; y++) {
      var r0 = y * N, r1 = r0 + N;
      for (x = 0; x < N - 1; x++) {
        var va = f[r0 + x], vb = f[r0 + x + 1], vc = f[r1 + x + 1], vd = f[r1 + x];
        var cs = (va > iso ? 1 : 0) | (vb > iso ? 2 : 0) | (vc > iso ? 4 : 0) | (vd > iso ? 8 : 0);
        if (cs === 0 || cs === 15) continue;
        // the four edges: top (a-b), right (b-c), bottom (d-c), left (a-d)
        var top = -1, right = -1, bottom = -1, left = -1, id;
        if ((cs & 1) !== ((cs >> 1) & 1)) { id = 2 * (r0 + x); ex[id] = x + (iso - va) / (vb - va); ey[id] = y; top = id; }
        if (((cs >> 1) & 1) !== ((cs >> 2) & 1)) { id = 2 * (r0 + x + 1) + 1; ex[id] = x + 1; ey[id] = y + (iso - vb) / (vc - vb); right = id; }
        if (((cs >> 3) & 1) !== ((cs >> 2) & 1)) { id = 2 * (r1 + x); ex[id] = x + (iso - vd) / (vc - vd); ey[id] = y + 1; bottom = id; }
        if ((cs & 1) !== ((cs >> 3) & 1)) { id = 2 * (r0 + x) + 1; ex[id] = x; ey[id] = y + (iso - va) / (vd - va); left = id; }
        var a1, b1, a2 = -1, b2 = -1;
        if (cs === 5 || cs === 10) {
          // saddle: the centre decides which corners connect
          var centre = (va + vb + vc + vd) * 0.25 > iso;
          if ((cs === 5) === centre) { a1 = top; b1 = right; a2 = bottom; b2 = left; }
          else { a1 = top; b1 = left; a2 = bottom; b2 = right; }
        } else {
          a1 = top >= 0 ? top : (right >= 0 ? right : bottom);
          // exactly two edges are crossed: the first of top/right/bottom, and
          // left, or else the remaining one of bottom/right
          b1 = left >= 0 ? left : (bottom >= 0 && bottom !== a1 ? bottom : right);
        }
        if (nb1[a1] < 0) nb1[a1] = b1; else nb2[a1] = b1;
        if (nb1[b1] < 0) nb1[b1] = a1; else nb2[b1] = a1;
        if (a2 >= 0) {
          if (nb1[a2] < 0) nb1[a2] = b2; else nb2[a2] = b2;
          if (nb1[b2] < 0) nb1[b2] = a2; else nb2[b2] = a2;
        }
      }
    }
    for (var pass = 0; pass < 2; pass++) {
      for (e = 0; e < 2 * NN; e++) {
        if (seen[e] || nb1[e] < 0) continue;
        if (pass === 0 && nb2[e] >= 0) continue;
        var pts = [], prev = -1, cur = e;
        while (cur >= 0 && !seen[cur]) {
          seen[cur] = 1;
          pts.push(ex[cur], ey[cur]);
          var nx = nb1[cur] !== prev ? nb1[cur] : nb2[cur];
          if (nb1[cur] === prev && nb2[cur] === prev) nx = -1;
          prev = cur; cur = nx;
        }
        raw.push({ p: pts, closed: pass === 1 });
      }
    }
  }
  // 1. luminance
  // per-stage timings, returned (cheap, and the only way to see the Pi)
  var clock = typeof performance !== 'undefined' ? performance : Date;
  var tLast = clock.now(), prof = {};
  var mark = function (name) { var t = clock.now(); prof[name] = Math.round((t - tLast) * 10) / 10; tLast = t; };
  var lum = new Uint8Array(NN);
  luminance(rgba, NN, lum);
  // Each stage is its own inner function: V8 optimises them one by one
  // (one giant function ran its loops in the interpreter on the Pi, 5 to 10
  // times slower), and nothing is allocated inside the per-pixel loops.
  mark('lum');
  var med = new Uint8Array(NN);
  medianHuang(lum, N, med);
  mark('median');
  var f = new Float32Array(NN);
  bilateral(med, N, f);

  mark('bilateral');
  // 2. thresholds
  var hist = new Uint32Array(256);
  histogram(f, NN, hist);
  function quantile(q) {
    var target = q * NN, acc = 0;
    for (var b = 0; b < 256; b++) { acc += hist[b]; if (acc >= target) return b; }
    return 255;
  }
  var lo = quantile(0.02), hi = quantile(0.98);
  var th = [quantile(0.25), quantile(0.5), quantile(0.75)];
  var MIN_SEP = 12;
  var bad = hi - lo < 2 * MIN_SEP;
  for (k = 0; k < 3 && !bad; k++) {
    if (th[k] - lo < MIN_SEP || hi - th[k] < MIN_SEP) bad = true;
    if (k > 0 && th[k] - th[k - 1] < MIN_SEP) bad = true;
  }
  if (bad) th = [lo + (hi - lo) * 0.25, lo + (hi - lo) * 0.5, lo + (hi - lo) * 0.75];
  var thresholds = [];
  if (hi - lo >= 2 * MIN_SEP) for (k = 0; k < 3; k++) thresholds.push(Math.floor(th[k]) + 0.5);
  var poster = new Uint8Array(NN);
  posterise(f, NN, thresholds, poster);

  mark('thresholds');
  // 3. marching squares, one iso level at a time (see isoContours)
  var raw = [];
  var ms = {
    ex: new Float32Array(2 * NN), ey: new Float32Array(2 * NN),
    nb1: new Int32Array(2 * NN), nb2: new Int32Array(2 * NN), seen: new Uint8Array(2 * NN)
  };
  for (var ti = 0; ti < thresholds.length; ti++) isoContours(f, N, thresholds[ti], ms, raw);

  mark('marching');
  // 4. lengths, areas, the first filter
  function polyLen(p, closed) {
    var L = 0, m = p.length / 2;
    for (var q = 1; q < m; q++) L += Math.hypot(p[2 * q] - p[2 * q - 2], p[2 * q + 1] - p[2 * q - 1]);
    if (closed && m > 1) L += Math.hypot(p[0] - p[2 * m - 2], p[1] - p[2 * m - 1]);
    return L;
  }
  var minLen = 0.06 * 4 * N;
  var smallArea = 0.004 * NN;
  var cands = [];
  var droppedSmall = 0, droppedLetters = 0, droppedText = 0, droppedSoft = 0;
  for (k = 0; k < raw.length; k++) {
    var r = raw[k];
    var L = polyLen(r.p, r.closed);
    if (L < minLen) { droppedSmall++; continue; }
    if (r.closed) {
      var A = 0, m2 = r.p.length / 2;
      for (j = 0; j < m2; j++) {
        var j2 = (j + 1) % m2;
        A += r.p[2 * j] * r.p[2 * j2 + 1] - r.p[2 * j2] * r.p[2 * j + 1];
      }
      A = Math.abs(A) * 0.5;
      if (A < smallArea && L * L / Math.max(A, 1e-6) > 18) { droppedLetters++; continue; }
    }
    // TEXT. Lines of letters merge into long contours that zigzag letter by
    // letter. Measured on 12 covers (see the test): the tortuosity, length
    // over length simplified at 1.5 px, is 1.00 to 1.088 on drawn edges and
    // 1.08 to 1.14 on lines of text; the total turning per px (on the 1-px
    // simplification) separates the overlap: a drawn edge under 0.20 rad/px
    // unless it is a tiny blob. Out: tortuosity over 1.095, or over 1.075
    // with turning over 0.20.
    var s1 = dp(r.closed ? r.p.concat([r.p[0], r.p[1]]) : r.p, 1.0), turn = 0;
    for (var q = 1; q < s1.length / 2 - 1; q++) {
      var a1 = Math.atan2(s1[2 * q + 1] - s1[2 * q - 1], s1[2 * q] - s1[2 * q - 2]);
      var a2 = Math.atan2(s1[2 * q + 3] - s1[2 * q + 1], s1[2 * q + 2] - s1[2 * q]);
      var da = a2 - a1; while (da > Math.PI) da -= 2 * Math.PI; while (da < -Math.PI) da += 2 * Math.PI;
      turn += Math.abs(da);
    }
    turn /= L;
    var tort = L / Math.max(1e-6, polyLen(dp(r.closed ? r.p.concat([r.p[0], r.p[1]]) : r.p, 1.5), false));
    if (tort > 1.095 || (tort > 1.075 && turn > 0.20)) { droppedText++; continue; }
    // EDGE STRENGTH: the mean gradient of the filtered field along the
    // contour, in levels per px. An iso-line through a soft gradient (sky,
    // road, skin shading) wanders and reads as noise: 1 to 15 measured,
    // against 19 to 120 for the edges a person would draw. Out under 16.
    var gs = 0, gn = 0;
    for (var q2 = 0; q2 < r.p.length / 2; q2 += 2) {
      var gx0 = Math.min(N - 2, Math.max(1, Math.round(r.p[2 * q2]))), gy0 = Math.min(N - 2, Math.max(1, Math.round(r.p[2 * q2 + 1])));
      var gxx = f[gy0 * N + gx0 + 1] - f[gy0 * N + gx0 - 1], gyy = f[(gy0 + 1) * N + gx0] - f[(gy0 - 1) * N + gx0];
      gs += 0.5 * Math.sqrt(gxx * gxx + gyy * gyy); gn++;
    }
    if (gs / gn < 16) { droppedSoft++; continue; }
    cands.push({ p: r.p, closed: r.closed, len: L });
  }
  cands.sort(function (a, b) { return b.len - a.len; });

  mark('filter');
  // 5-6. non-redundancy and the length budget
  var occ = new Uint8Array(NN);
  var RAD = 1.6;
  function covered(px, py) {
    var ix = Math.round(px), iy = Math.round(py);
    if (ix < 0 || iy < 0 || ix >= N || iy >= N) return false;
    return occ[iy * N + ix] === 1;
  }
  function stamp(p) {
    var r0 = Math.ceil(RAD), R2 = RAD * RAD;
    var m3 = p.length / 2;
    for (var q = 0; q < m3; q++) {
      // fill the gaps between vertices too (they are ~1 px apart)
      var steps = 1;
      if (q > 0) steps = Math.max(1, Math.ceil(Math.hypot(p[2 * q] - p[2 * q - 2], p[2 * q + 1] - p[2 * q - 1]) * 2));
      for (var s = 0; s < steps; s++) {
        var t = steps === 1 ? 1 : (s + 1) / steps;
        var cx = q > 0 ? p[2 * q - 2] + (p[2 * q] - p[2 * q - 2]) * t : p[0];
        var cy = q > 0 ? p[2 * q - 1] + (p[2 * q + 1] - p[2 * q - 1]) * t : p[1];
        var bx = Math.round(cx), by = Math.round(cy);
        for (var oy = -r0; oy <= r0; oy++) for (var ox = -r0; ox <= r0; ox++) {
          var gx = bx + ox, gy = by + oy;
          if (gx < 0 || gy < 0 || gx >= N || gy >= N) continue;
          var ddx2 = gx - cx, ddy2 = gy - cy;
          if (ddx2 * ddx2 + ddy2 * ddy2 <= R2) occ[gy * N + gx] = 1;
        }
      }
    }
  }
  var budget = 12 * N;
  var kept = [];
  var total = 0;
  var minRun = minLen;
  for (k = 0; k < cands.length && total < budget; k++) {
    var cd = cands[k];
    var m4 = cd.p.length / 2;
    var cov = new Uint8Array(m4);
    var any = false, all = true;
    for (j = 0; j < m4; j++) { cov[j] = covered(cd.p[2 * j], cd.p[2 * j + 1]) ? 1 : 0; if (cov[j]) any = true; else all = false; }
    if (all) continue;
    var runs = [];
    if (!any) runs.push({ p: cd.p, closed: cd.closed });
    else {
      // a closed contour is rotated to start on a covered point, so no run
      // is cut in two by the seam
      var start = 0;
      if (cd.closed) { while (!cov[start]) start++; }
      var cur2 = [];
      for (j = 0; j < m4; j++) {
        var jj = cd.closed ? (start + j) % m4 : j;
        if (!cov[jj]) cur2.push(cd.p[2 * jj], cd.p[2 * jj + 1]);
        else if (cur2.length) { runs.push({ p: cur2, closed: false }); cur2 = []; }
      }
      if (cur2.length) runs.push({ p: cur2, closed: false });
    }
    for (j = 0; j < runs.length; j++) {
      var rl = polyLen(runs[j].p, runs[j].closed);
      if (rl < minRun) continue;
      stamp(runs[j].p);
      if (runs[j].closed) stamp([runs[j].p[runs[j].p.length - 2], runs[j].p[runs[j].p.length - 1], runs[j].p[0], runs[j].p[1]]);
      kept.push({ p: runs[j].p, closed: runs[j].closed, len: rl });
      total += rl;
    }
  }

  mark('dedup');
  // 7. Douglas-Peucker 0.8 px, then Chaikin x2
  function dp(p, eps) {
    var m5 = p.length / 2;
    if (m5 < 3) return p.slice();
    var keep = new Uint8Array(m5);
    keep[0] = 1; keep[m5 - 1] = 1;
    var stack = [0, m5 - 1];
    while (stack.length) {
      var b = stack.pop(), a = stack.pop();
      var ax = p[2 * a], ay = p[2 * a + 1], bx2 = p[2 * b], by2 = p[2 * b + 1];
      var dxs = bx2 - ax, dys = by2 - ay, ll = Math.hypot(dxs, dys);
      var best = -1, bestD = eps;
      for (var q = a + 1; q < b; q++) {
        var d = ll > 1e-9 ? Math.abs((p[2 * q] - ax) * dys - (p[2 * q + 1] - ay) * dxs) / ll : Math.hypot(p[2 * q] - ax, p[2 * q + 1] - ay);
        if (d > bestD) { bestD = d; best = q; }
      }
      if (best >= 0) { keep[best] = 1; stack.push(a, best, best, b); }
    }
    var out = [];
    for (var q2 = 0; q2 < m5; q2++) if (keep[q2]) out.push(p[2 * q2], p[2 * q2 + 1]);
    return out;
  }
  function chaikin(p, closed) {
    var m6 = p.length / 2, out = [];
    if (m6 < 3) return p.slice();
    if (!closed) out.push(p[0], p[1]);
    var last = closed ? m6 : m6 - 1;
    for (var q = 0; q < last; q++) {
      var q1 = (q + 1) % m6;
      var ax = p[2 * q], ay = p[2 * q + 1], bx3 = p[2 * q1], by3 = p[2 * q1 + 1];
      out.push(0.75 * ax + 0.25 * bx3, 0.75 * ay + 0.25 * by3, 0.25 * ax + 0.75 * bx3, 0.25 * ay + 0.75 * by3);
    }
    if (!closed) out.push(p[2 * m6 - 2], p[2 * m6 - 1]);
    return out;
  }
  var strokes = [];
  for (k = 0; k < kept.length; k++) {
    var sp = kept[k].p;
    if (kept[k].closed) {
      // DP on a loop: split at the point farthest from the start
      var m7 = sp.length / 2, far = 0, fd = -1;
      for (j = 0; j < m7; j++) { var dd = Math.hypot(sp[2 * j] - sp[0], sp[2 * j + 1] - sp[1]); if (dd > fd) { fd = dd; far = j; } }
      var h1 = dp(sp.slice(0, 2 * far + 2), 0.8);
      var h2 = dp(sp.slice(2 * far).concat([sp[0], sp[1]]), 0.8);
      sp = h1.concat(h2.slice(2, h2.length - 2));
    } else sp = dp(sp, 0.8);
    sp = chaikin(chaikin(sp, kept[k].closed), kept[k].closed);
    strokes.push({ p: sp, closed: kept[k].closed, len: polyLen(sp, kept[k].closed) });
  }

  mark('simplify');
  // 8. order, the two sources, the orientation
  strokes.sort(function (a, b) { return b.len - a.len; });
  // the point budget: the shortest go first
  var MAXP = 4096;
  var npts = 0;
  for (k = 0; k < strokes.length; k++) npts += strokes[k].p.length / 2 + (strokes[k].closed ? 1 : 0);
  while (npts > MAXP && strokes.length) { var gone = strokes.pop(); npts -= gone.p.length / 2 + (gone.closed ? 1 : 0); }
  for (k = 0; k < strokes.length; k++) strokes[k].order = k;
  var srcLen = [0, 0], srcList = [[], []];
  for (k = 0; k < strokes.length; k++) {
    var si = srcLen[0] <= srcLen[1] ? 0 : 1;
    strokes[k].src = si; srcLen[si] += strokes[k].len; srcList[si].push(strokes[k]);
  }
  for (si = 0; si < 2; si++) {
    var endX = null, endY = null;
    for (k = 0; k < srcList[si].length; k++) {
      var st = srcList[si][k], pp = st.p, m8 = pp.length / 2;
      if (endX !== null) {
        if (st.closed) {
          var bi = 0, bd = Infinity;
          for (j = 0; j < m8; j++) { var d2 = Math.hypot(pp[2 * j] - endX, pp[2 * j + 1] - endY); if (d2 < bd) { bd = d2; bi = j; } }
          st.p = pp.slice(2 * bi).concat(pp.slice(0, 2 * bi));
        } else {
          var dA = Math.hypot(pp[0] - endX, pp[1] - endY), dB = Math.hypot(pp[2 * m8 - 2] - endX, pp[2 * m8 - 1] - endY);
          if (dB < dA) {
            var rev = [];
            for (j = m8 - 1; j >= 0; j--) rev.push(pp[2 * j], pp[2 * j + 1]);
            st.p = rev;
          }
        }
      }
      // a loop is drawn back to its first point
      if (st.closed) st.p = st.p.concat([st.p[0], st.p[1]]);
      endX = st.p[st.p.length - 2]; endY = st.p[st.p.length - 1];
    }
  }

  mark('order');
  // 9. colours, and the output
  function sampleRGB(px, py, out) {
    var ix = Math.max(0, Math.min(N - 1, Math.round(px))), iy = Math.max(0, Math.min(N - 1, Math.round(py)));
    var o = 4 * (iy * N + ix);
    out[0] = rgba[o]; out[1] = rgba[o + 1]; out[2] = rgba[o + 2];
    return out;
  }
  var s1 = [0, 0, 0], s2 = [0, 0, 0];
  var pts = new Float32Array(MAXP * 4), cum = new Float32Array(MAXP);
  var meta = [];
  var w0 = 0, run = 0, toN = 2 / (N - 1);
  for (si = 0; si < 2; si++) for (k = 0; k < srcList[si].length; k++) {
    var S = srcList[si][k], P = S.p, m9 = P.length / 2;
    var cr = 0, cg = 0, cb = 0, cn = 0, slen = 0;
    var first = w0;
    for (j = 0; j < m9; j++) {
      var px = P[2 * j], py = P[2 * j + 1];
      if (j > 0) { var seg = Math.hypot(px - P[2 * j - 2], py - P[2 * j - 1]) * toN; slen += seg; run += seg; }
      pts[4 * w0] = px * toN - 1; pts[4 * w0 + 1] = 1 - py * toN;
      pts[4 * w0 + 2] = S.order; pts[4 * w0 + 3] = j === 0 ? 1 : 0;
      cum[w0] = run;
      w0++;
      // the normal, from the neighbours
      var jA = Math.max(0, j - 1), jB = Math.min(m9 - 1, j + 1);
      var tx = P[2 * jB] - P[2 * jA], ty = P[2 * jB + 1] - P[2 * jA + 1], tl = Math.hypot(tx, ty) || 1;
      var nx2 = -ty / tl * 2, ny2 = tx / tl * 2;
      sampleRGB(px + nx2, py + ny2, s1); sampleRGB(px - nx2, py - ny2, s2);
      var ch1 = Math.max(s1[0], s1[1], s1[2]) - Math.min(s1[0], s1[1], s1[2]);
      var ch2 = Math.max(s2[0], s2[1], s2[2]) - Math.min(s2[0], s2[1], s2[2]);
      var sv2 = ch1 >= ch2 ? s1 : s2;
      cr += sv2[0]; cg += sv2[1]; cb += sv2[2]; cn++;
    }
    meta.push({ src: si, order: S.order, start: first, count: m9, len: slen, closed: S.closed, rgb: [cr / cn / 255, cg / cn / 255, cb / cn / 255] });
  }
  mark('colour');
  return {
    prof: prof, n: N, count: w0, pts: pts, cum: cum, strokes: meta, poster: poster,
    thresholds: thresholds, fallback: meta.length < 3,
    stats: { droppedSoft: droppedSoft, droppedText: droppedText, raw: raw.length, droppedSmall: droppedSmall, droppedLetters: droppedLetters, candidates: cands.length, kept: kept.length, lengthPx: total }
  };
}

// A synthetic cover (discs, bars, a gradient) to warm the worker's JIT: on
// the Pi a cold first extraction took 0.5 to 0.9 s, a warm one a fraction.
export function warmupImage(N) {
  var px = new Uint8Array(N * N * 4);
  for (var y = 0; y < N; y++) for (var x = 0; x < N; x++) {
    var v = (x * 255 / N) * 0.4;
    var d1 = Math.hypot(x - N * 0.35, y - N * 0.4), d2 = Math.hypot(x - N * 0.7, y - N * 0.65);
    if (d1 < N * 0.22) v = 230; if (d2 < N * 0.15) v = 20;
    if (y > N * 0.8 && (x % 24) < 10) v = 180;
    var o = 4 * (y * N + x);
    px[o] = v; px[o + 1] = v * 0.8; px[o + 2] = 255 - v; px[o + 3] = 255;
  }
  return px;
}

// The worker, built from the functions' own source. It warms up, then
// decodes each image itself (fetch + createImageBitmap at the target size +
// OffscreenCanvas) or takes the pixels.
export function contourWorkerSource() {
  return 'var extractContours = ' + extractContours.toString() + ';\n' +
    'var warmupImage = ' + warmupImage.toString() + ';\n' +
    // five passes: on the Pi the 2nd and 3rd real covers were still 2 to 4
    // times slower than the later ones after two
    'try { var __w = warmupImage(192); for (var __i = 0; __i < 5; __i++) extractContours(__w, 192); } catch (e) {}\n' +
    'self.onmessage = function (ev) {\n' +
    '  var m = ev.data;\n' +
    '  var t0 = performance.now();\n' +
    '  var go = function (rgba) {\n' +
    '    var t1 = performance.now();\n' +
    '    var res = extractContours(rgba, m.n);\n' +
    '    res.ms = performance.now() - t1; res.decodeMs = t1 - t0;\n' +
    '    self.postMessage({ id: m.id, res: res }, [res.pts.buffer, res.cum.buffer, res.poster.buffer]);\n' +
    '  };\n' +
    '  var fail = function (e) { self.postMessage({ id: m.id, error: String(e && e.message || e) }); };\n' +
    '  try {\n' +
    '    if (m.rgba) { go(m.rgba); return; }\n' +
    '    if (typeof OffscreenCanvas === "undefined" || typeof createImageBitmap === "undefined") { fail("nooffscreen"); return; }\n' +
    '    fetch(m.url).then(function (r) { if (!r.ok) throw new Error("http " + r.status); return r.blob(); })\n' +
    '      .then(function (b) {\n' +
    '        return createImageBitmap(b, { resizeWidth: m.n, resizeHeight: m.n, resizeQuality: "medium" })\n' +
    '          .catch(function () { return createImageBitmap(b); });\n' +
    '      })\n' +
    '      .then(function (bmp) {\n' +
    '        var c = new OffscreenCanvas(m.n, m.n), g = c.getContext("2d");\n' +
    '        g.drawImage(bmp, 0, 0, m.n, m.n);\n' +
    '        if (bmp.close) bmp.close();\n' +
    '        go(g.getImageData(0, 0, m.n, m.n).data);\n' +
    '      }).catch(fail);\n' +
    '  } catch (e) { fail(e); }\n' +
    '};\n';
}
